import React from "react";

import { SURVEYS } from "@typings/surveys";

import { useTelemetry } from "./useTelemetry";

const surveyData: SurveyData = {
  [SURVEYS.BOOKING_EXPERIENCE]: {},
};

type SurveyData = {
  [key in SURVEYS]: any;
};

const SurveyContext = React.createContext<{
  data: SurveyData | null;
  setData: React.Dispatch<React.SetStateAction<SurveyData>>;
}>({
  data: null,
  setData: () => undefined,
});

interface SurveyProviderProps {
  children: React.ReactNode;
}

const SurveyProvider = (props: SurveyProviderProps): React.ReactElement => {
  const [data, setData] = React.useState<SurveyData>(surveyData);

  return (
    <SurveyContext.Provider value={{ data, setData }}>
      {props.children}
    </SurveyContext.Provider>
  );
};

type SendSurvey = <T = { [key: string]: any }>(
  event: string,
  data: T,
  clearContext?: boolean,
) => void;

function useSurvey<T = { [key: string]: any }>(
  key: SURVEYS,
): {
  surveyContext: T;
  setSurveyContext: (data: T) => void;
  clearSurveyContext: () => void;
  sendSurvey: SendSurvey;
} {
  const { sendEvent } = useTelemetry();
  const context = React.useContext(SurveyContext);

  if (!SURVEYS[key]) {
    throw new Error(`The ${key} does not exits in the SURVEYS enum`);
  }

  if (context.data === null) {
    throw new Error("useSurvey must be used within SurveyProvider");
  }

  const surveyContext = context.data[key] as T;
  const setSurveyContext = (newData: T) =>
    context.setData({ ...context.data, [key]: newData });
  const clearSurveyContext = () =>
    context.setData({ ...context.data, [key]: {} });
  const sendSurvey: SendSurvey = (event, data, clearContext) => {
    sendEvent(event, {
      context: {
        surveyData: data,
        surveyContext: context.data?.[key] || {},
      },
    });

    if (clearContext) {
      clearSurveyContext();
    }
  };

  return {
    surveyContext,
    setSurveyContext,
    clearSurveyContext,
    sendSurvey,
  };
}

export { SurveyProvider, useSurvey };
